import { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { login, orgLogin, setupPassword, register, authConfig, listOrgs, CloudError } from '../lib/cloud';

type Mode = 'signin' | 'org' | 'register';

/**
 * Full-screen login for signed-out visitors.
 * Sign in (main admin / personal accounts), organization login, and — when
 * the main admin enabled it — self-serve registration.
 */
export function LoginPage() {
  const setAuth = useStore((s) => s.setAuth);
  const setView = useStore((s) => s.setView);
  const pushLine = useStore((s) => s.pushLine);

  const [mode, setMode] = useState<Mode>('signin');
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [username, setUsername] = useState('');
  const [orgPassword, setOrgPassword] = useState('');
  const [orgStep, setOrgStep] = useState<'pick' | 'creds' | 'setup'>('pick');
  const [orgList, setOrgList] = useState<string[]>([]);
  const [orgListState, setOrgListState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void authConfig()
      .then((c) => setSignupEnabled(c.signupEnabled))
      .catch(() => setSignupEnabled(false));
  }, []);

  useEffect(() => {
    if (mode !== 'org') return;
    setOrgStep('pick');
    setError(null);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'org' || orgStep !== 'pick') return;
    setOrgListState('loading');
    void listOrgs()
      .then((r) => { setOrgList(r.orgs); setOrgListState('ready'); })
      .catch(() => setOrgListState('error'));
  }, [mode, orgStep]);

  async function submitMain(): Promise<void> {
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (password.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    setBusy(true);
    try {
      const result = await login(email.trim(), password);
      setAuth(result.token, result.user);
      setView('home');
      pushLine('ok', `Signed in as ${result.user.name}${result.user.isAdmin ? ' (administrator)' : ''}.`);
      setPassword('');
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Authentication failed.');
    } finally {
      setBusy(false);
    }
  }

  async function submitOrg(): Promise<void> {
    setError(null);
    if (!orgName.trim()) {
      setError('Enter your organization name.');
      return;
    }
    if (!username.trim()) {
      setError('Enter your username.');
      return;
    }
    setBusy(true);
    try {
      const result = await orgLogin(orgName.trim(), username.trim(), orgPassword);
      if ('mustSetupPassword' in result && result.mustSetupPassword) {
        setSetupToken(result.setupToken);
        setNewPassword('');
        setConfirmPassword('');
        setOrgStep('setup');
        return;
      }
      if ('token' in result) {
        setAuth(result.token, result.user);
        setView('home');
        pushLine('ok', `Signed in as ${result.user.name} (organization "${result.user.orgName}").`);
        setOrgPassword('');
      }
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Authentication failed.');
    } finally {
      setBusy(false);
    }
  }

  async function submitSetup(): Promise<void> {
    setError(null);
    if (!setupToken) {
      setError('Setup session expired — start again from the sign-in page.');
      return;
    }
    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const result = await setupPassword(setupToken, newPassword);
      setAuth(result.token, result.user);
      setView('home');
      pushLine('ok', `Password saved. Signed in as ${result.user.name} (organization "${result.user.orgName}").`);
      setSetupToken(null);
      setOrgPassword('');
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not set the password.');
    } finally {
      setBusy(false);
    }
  }

  async function submitRegister(): Promise<void> {
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (password.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    setBusy(true);
    try {
      const result = await register(email.trim(), password);
      setAuth(result.token, result.user);
      setView('home');
      pushLine('ok', 'Welcome! Your account is ready.');
      setPassword('');
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Registration failed.');
    } finally {
      setBusy(false);
    }
  }

  function submitCurrent(): void {
    if (mode === 'signin') void submitMain();
    else if (mode === 'org') void submitOrg();
    else void submitRegister();
  }

  const cardTitle =
    mode === 'signin' ? 'SPblock'
    : mode === 'org' ? 'Organization login'
    : 'Create your account';

  const cardTagline =
    mode === 'signin' ? 'Sign in to reach your projects'
    : mode === 'org' ? 'Sign in with your organization account'
    : 'Registration is enabled by the main administrator';

  return (
    <div className="login-page">
      <div className="login-panel">
        <span className="wordmark-lg">SPblock</span>
        <span className="login-panel-tagline">Block coding for the ESP32</span>
      </div>

      <div className="login-form-side">
        <div className="login-card">
          <div className="login-brand">
            <span className="wordmark-lg">{cardTitle}</span>
            <span className="login-tagline">{cardTagline}</span>
          </div>

          <div className="auth-tabs">
            <button
              className={`auth-tab ${mode === 'signin' ? 'is-active' : ''}`}
              onClick={() => { setMode('signin'); setError(null); }}
            >
              Sign in
            </button>
            <button
              className={`auth-tab ${mode === 'org' ? 'is-active' : ''}`}
              onClick={() => { setMode('org'); setError(null); }}
            >
              Login with an organization
            </button>
            {signupEnabled && (
              <button
                className={`auth-tab ${mode === 'register' ? 'is-active' : ''}`}
                onClick={() => { setMode('register'); setError(null); }}
              >
                Create account
              </button>
            )}
          </div>

          {mode === 'signin' && (
            <>
              <div className="field">
                <div className="field-label"><span>Email</span></div>
                <input
                  type="email"
                  value={email}
                  autoComplete="username"
                  spellCheck={false}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitMain(); }}
                />
              </div>
              <div className="field">
                <div className="field-label">
                  <span>Password</span>
                </div>
                <input
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitMain(); }}
                />
              </div>
              {error && <div className="form-error err">{error}</div>}
              <button className="btn btn-primary btn-block" onClick={() => void submitMain()} disabled={busy}>
                {busy ? 'Working…' : 'Sign in'}
              </button>
            </>
          )}

          {mode === 'org' && orgStep === 'pick' && (
            <>
              <div className="field-label"><span>Choose your organization</span></div>
              {orgListState === 'loading' && (
                <p className="field-note">Loading organizations…</p>
              )}
              {orgListState === 'error' && (
                <p className="form-error err">Could not load the organization list. Go back and try again.</p>
              )}
              {orgListState === 'ready' && orgList.length === 0 && (
                <p className="field-note">No organizations are registered yet.</p>
              )}
              {orgListState === 'ready' && orgList.length > 0 && (
                <div className="org-picker">
                  {orgList.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="btn btn-block"
                      style={{ justifyContent: 'flex-start', marginBottom: 8 }}
                      onClick={() => { setOrgName(name); setOrgStep('creds'); setError(null); }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {mode === 'org' && orgStep === 'creds' && (
            <>
              <div className="field-label" style={{ marginBottom: 10 }}>
                <span>Signing in to <strong>{orgName}</strong></span>
                <button
                  type="button"
                  className="field-hint"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
                  onClick={() => { setOrgStep('pick'); setUsername(''); setOrgPassword(''); setError(null); }}
                >
                  Change organization
                </button>
              </div>
              <div className="field">
                <div className="field-label"><span>Username</span></div>
                <input
                  type="text"
                  value={username}
                  autoComplete="username"
                  spellCheck={false}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitOrg(); }}
                />
              </div>
              <div className="field">
                <div className="field-label">
                  <span>Password</span>
                </div>
                <input
                  type="password"
                  value={orgPassword}
                  autoComplete="current-password"
                  onChange={(e) => setOrgPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitOrg(); }}
                />
              </div>
              {error && <div className="form-error err">{error}</div>}
              <button className="btn btn-primary btn-block" onClick={() => void submitOrg()} disabled={busy}>
                {busy ? 'Working…' : 'Sign in to organization'}
              </button>
            </>
          )}

          {mode === 'org' && orgStep === 'setup' && (
            <>
              <div className="field-label" style={{ marginBottom: 10 }}>
                <span>Welcome, <strong>{username}</strong> — set your password</span>
              </div>
              <div className="field">
                <div className="field-label"><span>New password</span></div>
                <input
                  type="password"
                  value={newPassword}
                  autoComplete="new-password"
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitSetup(); }}
                />
              </div>
              <div className="field">
                <div className="field-label"><span>Confirm password</span></div>
                <input
                  type="password"
                  value={confirmPassword}
                  autoComplete="new-password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitSetup(); }}
                />
              </div>
              {error && <div className="form-error err">{error}</div>}
              <button className="btn btn-primary btn-block" onClick={() => void submitSetup()} disabled={busy}>
                {busy ? 'Working…' : 'Save password and sign in'}
              </button>
            </>
          )}

          {mode === 'register' && (
            <>
              <div className="field">
                <div className="field-label"><span>Email</span></div>
                <input
                  type="email"
                  value={email}
                  autoComplete="username"
                  spellCheck={false}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitRegister(); }}
                />
              </div>
              <div className="field">
                <div className="field-label">
                  <span>Password</span>
                </div>
                <input
                  type="password"
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitRegister(); }}
                />
              </div>
              {error && <div className="form-error err">{error}</div>}
              <button className="btn btn-primary btn-block" onClick={() => void submitRegister()} disabled={busy}>
                {busy ? 'Working…' : 'Create account'}
              </button>
            </>
          )}
          <p className="login-legal">
            <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>
            {'\u00A0\u00B7\u00A0'}
            <a href="/terms.html" target="_blank" rel="noreferrer">Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
}
